import { CLEARED_MARKETPLACE_FILTERS } from '../../constants/marketplace';
import { IGetFormDataRequestMeta, ISelectionScope } from '../../types/country';
import {
  IMarketplaceCartLine,
  IMarketplaceCatalogItem,
  IMarketplaceFilters,
  IMarketplaceMetadata,
  IMarketplaceProductDetails,
  MarketplaceDetailsStatusEnum,
} from '../../types/marketplace';
import {
  ActionsEnum,
  IAction,
  IError,
  IMarketplaceCatalogRequestPayload,
  IMarketplaceCatalogSuccessPayload,
  IMarketplaceHydrateItemFailurePayload,
  IMarketplaceHydrateItemsPayload,
  IMarketplaceHydrateItemSuccessPayload,
  IMarketplaceProductFailurePayload,
  IMarketplaceState,
} from '../../types/store';
import { resolveCartAfterRemoval } from '../../util/credits';
import { buildCatalogContext } from '../../util/marketplace';

const initialState: IMarketplaceState = {
  items: [],
  metadata: null,
  filters: CLEARED_MARKETPLACE_FILTERS,
  nextCursor: null,
  cursorBudget: null,
  catalogContext: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  details: {},
  detailsStatus: {},
  cart: [],
  openProductId: null,
  isCartOpen: false,
  swapPending: null,
  replacedCart: null,
  isSupplierFilterOn: false,
  cacheValue: null,
  suspendedCart: null,
};

const withStatus = (
  state: IMarketplaceState,
  productId: string,
  status: MarketplaceDetailsStatusEnum,
): IMarketplaceState['detailsStatus'] => ({ ...state.detailsStatus, [productId]: status });

const appendUniqueItems = (
  current: IMarketplaceCatalogItem[],
  incoming: IMarketplaceCatalogItem[],
): IMarketplaceCatalogItem[] => {
  const seen = new Set(current.map(({ product_id }) => product_id));

  return [...current, ...incoming.filter(({ product_id }) => !seen.has(product_id))];
};

const reducer = (state: IMarketplaceState = initialState, action: IAction): IMarketplaceState => {
  switch (action.type) {
    case ActionsEnum.GetFormDataRequest: {
      return (action.meta as IGetFormDataRequestMeta | null)?.countryChange ? state : initialState;
    }
    case ActionsEnum.MarketplaceCatalogRequest: {
      const { isLoadMore } = (action.payload as IMarketplaceCatalogRequestPayload) || {};

      return {
        ...state,
        catalogContext: isLoadMore ? state.catalogContext : buildCatalogContext(state.filters, state.cart),
        isLoading: !isLoadMore,
        isLoadingMore: !!isLoadMore,
        error: null,
      };
    }
    case ActionsEnum.MarketplaceCatalogSuccess: {
      const { items, nextCursor, budget, isLoadMore } = action.payload as IMarketplaceCatalogSuccessPayload;

      return {
        ...state,
        items: isLoadMore ? appendUniqueItems(state.items, items) : items,
        nextCursor,
        cursorBudget: budget,
        isLoading: false,
        isLoadingMore: false,
      };
    }
    case ActionsEnum.MarketplaceCatalogFailure: {
      return { ...state, isLoading: false, isLoadingMore: false, error: action.payload as IError };
    }
    case ActionsEnum.MarketplaceOpenDetails: {
      return { ...state, openProductId: action.payload as string };
    }
    case ActionsEnum.MarketplaceCloseDetails: {
      return { ...state, openProductId: null };
    }
    case ActionsEnum.MarketplaceSetCartOpen: {
      return { ...state, isCartOpen: action.payload as boolean };
    }
    case ActionsEnum.MarketplaceMetadataSuccess: {
      return { ...state, metadata: action.payload as IMarketplaceMetadata };
    }
    case ActionsEnum.MarketplaceSetFilters: {
      return {
        ...state,
        filters: { ...state.filters, ...(action.payload as Partial<IMarketplaceFilters>) },
        nextCursor: null,
      };
    }
    case ActionsEnum.MarketplaceProductRequest: {
      const productId = action.payload as string;

      return { ...state, detailsStatus: withStatus(state, productId, MarketplaceDetailsStatusEnum.Loading) };
    }
    case ActionsEnum.MarketplaceProductSuccess: {
      const details = action.payload as IMarketplaceProductDetails;

      return {
        ...state,
        details: { ...state.details, [details.product_id]: details },
        detailsStatus: withStatus(state, details.product_id, MarketplaceDetailsStatusEnum.Loaded),
      };
    }
    case ActionsEnum.MarketplaceProductFailure: {
      const { productId } = action.payload as IMarketplaceProductFailurePayload;

      return { ...state, detailsStatus: withStatus(state, productId, MarketplaceDetailsStatusEnum.Failed) };
    }
    case ActionsEnum.MarketplaceHydrateItemsRequest: {
      const { productIds } = action.payload as IMarketplaceHydrateItemsPayload;

      const detailsStatus = { ...state.detailsStatus };

      productIds.forEach((productId) => {
        detailsStatus[productId] = MarketplaceDetailsStatusEnum.Loading;
      });

      return { ...state, detailsStatus };
    }
    case ActionsEnum.MarketplaceHydrateItemSuccess: {
      const { productId, details } = action.payload as IMarketplaceHydrateItemSuccessPayload;

      return {
        ...state,
        details: { ...state.details, [productId]: details },
        detailsStatus: withStatus(state, productId, MarketplaceDetailsStatusEnum.Loaded),
      };
    }
    case ActionsEnum.MarketplaceHydrateItemFailure: {
      const { productId } = action.payload as IMarketplaceHydrateItemFailurePayload;

      return { ...state, detailsStatus: withStatus(state, productId, MarketplaceDetailsStatusEnum.Failed) };
    }
    case ActionsEnum.MarketplaceAddToCart: {
      const line = action.payload as IMarketplaceCartLine;

      const existingIndex = state.cart.findIndex((cartLine) => cartLine.product_id === line.product_id);
      const cart =
        existingIndex === -1
          ? [...state.cart, line]
          : state.cart.map((cartLine, index) => (index === existingIndex ? line : cartLine));
      const isSwapTarget = state.swapPending?.product_id === line.product_id;

      return {
        ...state,
        cart,
        swapPending: isSwapTarget ? null : state.swapPending,
        replacedCart: null,
        suspendedCart: null,
      };
    }
    case ActionsEnum.MarketplaceRemoveFromCart: {
      const variantId = action.payload as string;
      const { cart, swapped } = resolveCartAfterRemoval(state.cart, variantId, state.swapPending);

      return {
        ...state,
        cart,
        swapPending: swapped ? null : state.swapPending,
        replacedCart: null,
        isSupplierFilterOn: cart.length ? state.isSupplierFilterOn : false,
      };
    }
    case ActionsEnum.MarketplaceClearCart: {
      return { ...state, cart: [], swapPending: null, replacedCart: null, isSupplierFilterOn: false };
    }
    case ActionsEnum.MarketplaceReplaceCart: {
      return {
        ...state,
        cart: [action.payload as IMarketplaceCartLine],
        replacedCart: state.cart,
        swapPending: null,
        isSupplierFilterOn: false,
        suspendedCart: null,
      };
    }
    case ActionsEnum.MarketplaceRestoreCart: {
      if (!state.replacedCart) {
        return state;
      }

      return { ...state, cart: state.replacedCart, replacedCart: null, swapPending: null, suspendedCart: null };
    }
    case ActionsEnum.MarketplaceDropReplacedCart: {
      return { ...state, replacedCart: null };
    }
    case ActionsEnum.MarketplaceSwapStart: {
      return { ...state, swapPending: action.payload as IMarketplaceCartLine, isCartOpen: true, replacedCart: null };
    }
    case ActionsEnum.MarketplaceSwapCancel: {
      return { ...state, swapPending: null };
    }
    case ActionsEnum.MarketplaceSetSupplierFilter: {
      return { ...state, isSupplierFilterOn: action.payload as boolean };
    }
    case ActionsEnum.MarketplaceSetCacheValue: {
      return { ...state, cacheValue: action.payload as string };
    }
    case ActionsEnum.SetPickedCampaignId: {
      return action.payload
        ? { ...state, cart: [], swapPending: null, replacedCart: null, isSupplierFilterOn: false, suspendedCart: null }
        : state;
    }
    case ActionsEnum.SetKitPicked: {
      if (!action.payload) {
        return state;
      }

      return {
        ...state,
        cart: [],
        swapPending: null,
        replacedCart: null,
        isSupplierFilterOn: false,
        suspendedCart: null,
      };
    }
    case ActionsEnum.SubmitKit: {
      return state.suspendedCart ? { ...state, suspendedCart: null } : state;
    }
    case ActionsEnum.SuspendMarketplaceSelection: {
      const { cart } = action.payload as ISelectionScope;

      if (!cart || !state.cart.length) {
        return state;
      }

      return {
        ...state,
        suspendedCart: state.cart,
        cart: [],
        swapPending: null,
        replacedCart: null,
        isSupplierFilterOn: false,
      };
    }
    case ActionsEnum.RestoreMarketplaceSelection: {
      const { cart } = action.payload as ISelectionScope;

      if (!cart || !state.suspendedCart) {
        return state;
      }

      return { ...state, cart: state.suspendedCart, suspendedCart: null };
    }
    case ActionsEnum.DropSuspendedSelection: {
      return state.suspendedCart ? { ...state, suspendedCart: null } : state;
    }
    default: {
      return state;
    }
  }
};

export default reducer;
